import { PMBadge, PMIcon } from "@/ds/vendor";
import type { Observation, Visit } from "@/data/queries";
import { useMediaUrl } from "@/data/media";
import { levelDef } from "@/lib/levels";
import { useApp } from "@/app/AppContext";
import { STATUS_META, visitStatus } from "./visitStatus";
import { clock, duration, relTime } from "./format";

export function VisitStatusBadge({ v }: { v: Parameters<typeof visitStatus>[0] }) {
  const s = visitStatus(v);
  const m = STATUS_META[s];
  return <PMBadge variant={m.badge} dot uppercase>{m.label}</PMBadge>;
}

export type ObsKind = "photo" | "audio" | "video" | "text";
export function obsKind(o: Pick<Observation, "content_type">): ObsKind {
  const t = o.content_type || "";
  if (t.startsWith("image/")) return "photo";
  if (t.startsWith("audio/")) return "audio";
  if (t.startsWith("video/")) return "video";
  return "text";
}
export const KIND_META: Record<ObsKind, { label: string; emoji: string; icon: string }> = {
  photo: { label: "Photo", emoji: "📷", icon: "camera-outline" },
  audio: { label: "Audio", emoji: "🎙️", icon: "mic-outline" },
  video: { label: "Video", emoji: "🎥", icon: "play-outline" },
  text: { label: "Note", emoji: "📝", icon: "document-outline" },
};

export function TypePill({ kind, count }: { kind: ObsKind; count?: number }) {
  const m = KIND_META[kind];
  const cls = kind === "text" ? "tpill-text" : `tpill-${kind}`;
  return <span className={`tpill ${cls}`}>{m.emoji} {count !== undefined ? `${count} ` : ""}{m.label}{count !== undefined && count !== 1 ? "s" : ""}</span>;
}

// Field-visit card (preview/components-fieldvisit-card.html).
export function VisitCard({ v, onOpen, showLocation = true }: { v: Visit; onOpen: () => void; showLocation?: boolean }) {
  const { hierarchy } = useApp();
  const d = levelDef(v.location_level, hierarchy.key);
  const notes = Math.max(0, (v.obs_count ?? 0) - (v.photo_count ?? 0) - (v.audio_count ?? 0));
  const dur = duration(v.started_at, v.completed_at);
  return (
    <button className="vcard pm-lift pm-press" onClick={onOpen}>
      <div className="vcard-top">
        <VisitStatusBadge v={v} />
        <span className="spacer" />
        <span className="hint">{relTime(v.completed_at || v.started_at)}</span>
      </div>
      <h3 className="vcard-title">{v.title || "Field visit"}</h3>
      <div className="meta">
        {showLocation && v.location_label && <span>{d.emoji} {v.location_label}</span>}
        {showLocation && v.location_label && <span className="sep" />}
        <span>Started {clock(v.started_at)}</span>
        {dur && <><span className="sep" /><span>{dur}</span></>}
      </div>
      {v.ai_summary ? <p className="vcard-summary clamp-3">{v.ai_summary}</p> : <p className="vcard-summary subtle">{visitStatus(v) === "processing" ? "Summary on its way. Transcribing and summarizing observations." : "No summary yet."}</p>}
      <div className="vcard-counts">
        {!!v.photo_count && <TypePill kind="photo" count={v.photo_count} />}
        {!!v.audio_count && <TypePill kind="audio" count={v.audio_count} />}
        {notes > 0 && <TypePill kind="text" count={notes} />}
      </div>
    </button>
  );
}

// Compact list row variant for dense lists.
export function VisitRow({ v, onOpen }: { v: Visit; onOpen: () => void }) {
  const { hierarchy } = useApp();
  const d = levelDef(v.location_level, hierarchy.key);
  return (
    <button className="list-row" onClick={onOpen}>
      <span className="emoji-disc">{d.emoji}</span>
      <span className="list-main">
        <span className="list-title">{v.title || "Field visit"}</span>
        <span className="meta">{v.location_label}<span className="sep" />{relTime(v.completed_at || v.started_at)}<span className="sep" />{v.obs_count ?? 0} obs</span>
      </span>
      <VisitStatusBadge v={v} />
      <span className="chev"><PMIcon name="chevron-right" size={18} /></span>
    </button>
  );
}

export function MediaThumb({ o, alt = 0 }: { o: Observation; alt?: number }) {
  const url = useMediaUrl(obsKind(o) === "photo" ? o.storage_key : null);
  if (url) return <img src={url} alt={o.image_text_ai || o.whatsapp_message_content_ai || "Observation photo"} loading="lazy" />;
  return <div className={`media-ph ${alt % 3 ? `alt${alt % 3}` : ""}`} aria-label="Photo placeholder">📷</div>;
}
